<template>
  <div class="water-on-paper-parameters">
    <b-row
      v-if="isLoading"
      class="water-on-paper-parameters__loader"
      align-h="center"
      align-v="center"
    >
      <s-loader size="lg" />
    </b-row>
    <div v-if="!isLoading && parameters">
      <div class="water-on-paper-parameters-general mb-3">
        <div class="water-on-paper-parameters-general__title">{{ $t('general.general') }}</div>
        <div class="p-3">
          <b-row class="mb-4">
            <b-col
              sm="7"
              md="8"
            >
              <label>
                {{ getLabel('equilibriumCurve') }}<span v-if="editMode">*</span>
              </label>
              <div class="water-on-paper-parameters-general__select form-group">
                <s-input-label-edit
                  v-model="$v.parameters.equilibriumCurve.$model"
                  :formatted-value="$v.parameters.equilibriumCurve.$model
                   | enumFilter('engineering.water-on-paper')"
                  :editable="editMode"
                >
                  <s-enum-select
                    v-model="$v.parameters.equilibriumCurve.$model"
                    no-select-option
                    no-sort
                    translation-path="enums.engineering.water-on-paper"
                    :enum-definition="equilibriumCurve"
                    :disabled="isDisabled"
                  />
                  <b-form-invalid-feedback
                    v-if="$v.parameters.equilibriumCurve.$error"
                    id="input-live-feedback-equilibrium-curve"
                  >
                    <span v-html="getValidationFeedback('parameters', 'equilibriumCurve')"></span>
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </div>
            </b-col>
            <b-col
              sm="5"
              md="4"
            >
              <label>
                {{ getLabel('gasVolume') }}<span v-if="editMode">*</span>
              </label>
              <div class="
                water-on-paper-parameters-general__input
                form-group position-relative
                water-on-paper-parameters-general__gas-volume-input
              ">
                <s-input-label-edit
                  v-model="$v.parameters.gasVolume.$model"
                  :formatted-value="formatNumberValue(
                      $v.parameters.gasVolume.$model,
                      'gasVolume'
                    )"
                  :editable="editMode"
                >
                  <s-number-input
                    class="text-right"
                    v-model="$v.parameters.gasVolume.$model"
                    :numeric-options="getNumericOptions('gasVolume')"
                    :validation="$v.parameters.gasVolume"
                    :invalid="$v.parameters.gasVolume.$error"
                    :disabled="isDisabled"
                  />
                  <b-form-invalid-feedback
                    v-if="$v.parameters.gasVolume.$error"
                    id="input-live-feedback-gas-volume"
                  >
                    <span v-html="getValidationFeedback('parameters', 'gasVolume')"></span>
                  </b-form-invalid-feedback>
                </s-input-label-edit>
                <div
                  v-if="editMode"
                  class="d-inline-block water-on-paper-parameters-general__calculator"
                >
                  <b-button
                    class="water-on-paper-parameters-general__calculator-button no-internal-print"
                    variant="outline-primary"
                    size="sm"
                    :disabled="isSaving"
                    @click="onClickVgCalculatorHandler"
                  >
                    <i
                      class="water-on-paper-parameters-general__calculator-icon fas fa-calculator"
                      aria-hidden="true"
                    />
                  </b-button>
                  <info-badge
                    class="water-on-paper-parameters-general__calculator-tooltip no-internal-print"
                    :translation-key="'messages.MI031.message'"
                  />
                </div>
              </div>
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col>
              <b-checkbox
                v-model="$v.parameters.shouldConsiderStabilizationTime.$model"
                :disabled="isDisabled"
              >
                {{ getLabel('shouldConsiderStabilizationTime') }}
              </b-checkbox>
            </b-col>
          </b-row>
          <div
            v-if="$v.parameters.shouldConsiderStabilizationTime.$model"
            class="water-on-paper-parameters-general__box p-3 mb-3"
          >
            <label>
              {{ $t('engineering.water-on-paper.parameters-tab.dynamic-model') }}
            </label>
            <b-row>
              <b-col
                sm="7"
                md="8"
              >
                <label>
                  {{ getLabel('paperActivationEnergy') }}<span v-if="editMode">*</span>
                </label>
                <div class="water-on-paper-parameters-general__input form-group">
                  <s-input-label-edit
                    v-model="$v.parameters.paperActivationEnergy.$model"
                    :formatted-value="formatNumberValue(
                      $v.parameters.paperActivationEnergy.$model,
                      'paperActivationEnergy'
                    )"
                    :editable="editMode"
                  >
                    <s-number-input
                      class="text-right"
                      v-model="$v.parameters.paperActivationEnergy.$model"
                      :numeric-options="getNumericOptions('paperActivationEnergy')"
                      :validation="$v.parameters.paperActivationEnergy"
                      :invalid="$v.parameters.paperActivationEnergy.$error"
                      :disabled="isDisabled"
                    />
                    <b-form-invalid-feedback
                      v-if="$v.parameters.paperActivationEnergy.$error"
                      id="input-live-feedback-paper-activation-energy"
                    >
                      <span v-html="getValidationFeedback(
                        'parameters',
                        'paperActivationEnergy'
                      )"></span>
                    </b-form-invalid-feedback>
                  </s-input-label-edit>
                </div>
              </b-col>
              <b-col
                sm="5"
                md="4"
              >
                <label>
                  {{ getLabel('paperThickness') }}<span v-if="editMode">*</span>
                </label>
                <div class="water-on-paper-parameters-general__input form-group">
                  <s-input-label-edit
                    v-model="$v.parameters.paperThickness.$model"
                    :formatted-value="formatNumberValue(
                      $v.parameters.paperThickness.$model,
                      'paperThickness'
                    )"
                    :editable="editMode"
                  >
                    <s-number-input
                      class="text-right"
                      v-model="$v.parameters.paperThickness.$model"
                      :numeric-options="getNumericOptions('paperThickness')"
                      :validation="$v.parameters.paperThickness"
                      :invalid="$v.parameters.paperThickness.$error"
                      :disabled="isDisabled"
                    />
                    <b-form-invalid-feedback
                      v-if="$v.parameters.paperThickness.$error"
                      id="input-live-feedback-paper-thickness"
                    >
                      <span v-html="getValidationFeedback('parameters', 'paperThickness')"></span>
                    </b-form-invalid-feedback>
                  </s-input-label-edit>
                </div>
              </b-col>
            </b-row>
          </div>
          <div class="water-on-paper-parameters-general__box p-3 mb-4">
            <b-row class="mb-3">
              <b-col>
                <b-checkbox
                  v-model="$v.parameters.shouldConsiderOilType.$model"
                  :disabled="isDisabled"
                  @change="onChangeConsiderOilTypeHandler"
                >
                  {{ getLabel('shouldConsiderOilType') }}
                </b-checkbox>
              </b-col>
            </b-row>
            <b-row>
              <b-col
                sm="7"
                md="8"
              >
                <label>
                  {{ getLabel('oilSolubilityConstant') }}<span v-if="editMode">*</span>
                </label>
                <div class="water-on-paper-parameters-general__input form-group">
                  <s-input-label-edit
                    v-model="$v.parameters.oilSolubilityConstant.$model"
                    :formatted-value="formatNumberValue(
                      $v.parameters.oilSolubilityConstant.$model,
                      'oilSolubilityConstant'
                    )"
                    :editable="editMode"
                  >
                    <s-number-input
                      class="text-right"
                      v-model="$v.parameters.oilSolubilityConstant.$model"
                      :numeric-options="getNumericOptions('oilSolubilityConstant')"
                      :validation="$v.parameters.oilSolubilityConstant"
                      :invalid="$v.parameters.oilSolubilityConstant.$error"
                      :disabled="shouldDisableOilSolubilityConstant"
                    />
                    <b-form-invalid-feedback
                      v-if="$v.parameters.oilSolubilityConstant.$error"
                      id="input-live-feedback-oil-solubility-constant"
                    >
                      <span v-html="getValidationFeedback(
                        'parameters',
                        'oilSolubilityConstant'
                      )"></span>
                    </b-form-invalid-feedback>
                  </s-input-label-edit>
                </div>
              </b-col>
            </b-row>
          </div>
          <b-row>
            <b-col
              sm="7"
              md="8"
            >
              <label>
                {{ getLabel('atmosphericPressure') }}<span v-if="editMode">*</span>
                <info-badge :translation-key="'messages.MI033.message'" />
              </label>
              <div class="water-on-paper-parameters-general__input form-group">
                <s-input-label-edit
                  v-model="$v.parameters.atmosphericPressure.$model"
                  :formatted-value="formatNumberValue(
                    $v.parameters.atmosphericPressure.$model,
                    'atmosphericPressure'
                  )"
                  :editable="editMode"
                >
                  <s-number-input
                    class="text-right"
                    v-model="$v.parameters.atmosphericPressure.$model"
                    :numeric-options="getNumericOptions('atmosphericPressure')"
                    :validation="$v.parameters.atmosphericPressure"
                    :invalid="$v.parameters.atmosphericPressure.$error"
                    :disabled="isDisabled"
                  />
                  <b-form-invalid-feedback
                    v-if="$v.parameters.atmosphericPressure.$error"
                    id="input-live-feedback-atmospheric-pressure"
                  >
                    <span v-html="getValidationFeedback(
                      'parameters',
                      'atmosphericPressure'
                    )"></span>
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </div>
            </b-col>
            <b-col
              sm="5"
              md="4"
            >
              <label>
                {{ getLabel('referenceTemperature') }}<span v-if="editMode">*</span>
              </label>
              <div class="water-on-paper-parameters-general__input form-group">
                <s-input-label-edit
                  v-model="$v.parameters.referenceTemperature.$model"
                  :formatted-value="formatNumberValue(
                    $v.parameters.referenceTemperature.$model,
                    'referenceTemperature'
                  )"
                  :editable="editMode"
                >
                  <s-number-input
                    class="text-right"
                    v-model="$v.parameters.referenceTemperature.$model"
                    :numeric-options="getNumericOptions('referenceTemperature')"
                    :validation="$v.parameters.referenceTemperature"
                    :invalid="$v.parameters.referenceTemperature.$error"
                    :disabled="isDisabled"
                  />
                  <b-form-invalid-feedback
                    v-if="$v.parameters.referenceTemperature.$error"
                    id="input-live-feedback-reference-temperature"
                  >
                    <span v-html="getValidationFeedback(
                      'parameters',
                      'referenceTemperature'
                    )"></span>
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col
              sm="7"
              md="8"
            >
              <label>
                {{ getLabel('estimatedHeightBetweenConservatorAndWindingTop') }}<!--
                --><span v-if="editMode">*</span>
              </label>
              <div class="water-on-paper-parameters-general__input form-group">
                <s-input-label-edit
                  v-model="$v.parameters.estimatedHeightBetweenConservatorAndWindingTop.$model"
                  :formatted-value="formatNumberValue(
                    $v.parameters.estimatedHeightBetweenConservatorAndWindingTop.$model,
                    'estimatedHeightBetweenConservatorAndWindingTop'
                  )"
                  :editable="editMode"
                >
                  <s-number-input
                    class="text-right"
                    v-model="
                      $v.parameters.estimatedHeightBetweenConservatorAndWindingTop.$model
                    "
                    :numeric-options="getNumericOptions(
                      'estimatedHeightBetweenConservatorAndWindingTop'
                    )"
                    :validation="$v.parameters.estimatedHeightBetweenConservatorAndWindingTop"
                    :invalid="$v.parameters.estimatedHeightBetweenConservatorAndWindingTop.$error"
                    :disabled="isDisabled"
                  />
                  <b-form-invalid-feedback
                    v-if="$v.parameters.estimatedHeightBetweenConservatorAndWindingTop.$error"
                    id="input-live-feedback-estimated-height"
                  >
                    <span v-html="getValidationFeedback(
                      'parameters',
                      'estimatedHeightBetweenConservatorAndWindingTop'
                    )"></span>
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
      <div
        v-show="windings.length"
        class="water-on-paper-parameters-general"
      >
        <div
          v-for="(winding, index) in windings"
          class="avoid-break"
          fluid
          :class="windingClassMarginBottom(index)"
          :key="index"
        >
          <div class="water-on-paper-parameters-general-windings">
            <div class="water-on-paper-parameters-general__title">
              {{ `${$t('engineering.water-on-paper.parameters-tab.winding')} ${index + 1}` }}
            </div>
            <div class="p-3">
              <b-row>
                <b-col
                  sm="7"
                  md="8"
                >
                  <label>
                    {{ getLabel('bubbleFormationMinimumTemperatureDifference') }}<!--
                    --><span v-if="editMode">*</span>
                  </label>
                  <div class="water-on-paper-parameters-general__input form-group">
                    <s-input-label-edit
                      v-model="$v.windings
                      .$each[index]
                      .bubbleFormationMinimumTemperatureDifference
                      .$model"
                      :formatted-value="formatNumberValue(
                      $v.windings.$each[index].bubbleFormationMinimumTemperatureDifference.$model,
                      'bubbleFormationMinimumTemperatureDifference'
                    )"
                      :editable="editMode"
                    >
                      <s-number-input
                        class="text-right"
                        v-model="
                        $v.windings.$each[index].bubbleFormationMinimumTemperatureDifference.$model
                        "
                        :numeric-options="getNumericOptions(
                          'bubbleFormationMinimumTemperatureDifference'
                        )"
                        :validation="$v.windings
                          .$each[index]
                          .bubbleFormationMinimumTemperatureDifference"
                        :invalid="$v.windings
                          .$each[index]
                          .bubbleFormationMinimumTemperatureDifference
                          .$error"
                        :disabled="isDisabled"
                      />
                      <b-form-invalid-feedback
                        v-if="$v.windings
                        .$each[index]
                        .bubbleFormationMinimumTemperatureDifference
                        .$error"
                        id="input-live-feedback-bubble-formation"
                      >
                        <span v-html="getValidationFeedback(
                          'windings',
                          'bubbleFormationMinimumTemperatureDifference',
                          index
                        )"></span>
                      </b-form-invalid-feedback>
                    </s-input-label-edit>
                  </div>
                </b-col>
                <b-col
                  sm="5"
                  md="4"
                >
                  <label>
                    {{ getLabel('admissibleWaterOnPaper') }}<span v-if="editMode">*</span>
                  </label>
                  <div class="water-on-paper-parameters-general__input form-group">
                    <s-input-label-edit
                      v-model="$v.windings.$each[index].admissibleWaterOnPaper.$model"
                      :formatted-value="formatNumberValue(
                      $v.windings.$each[index].admissibleWaterOnPaper.$model,
                      'admissibleWaterOnPaper'
                    )"
                      :editable="editMode"
                    >
                      <s-number-input
                        class="text-right"
                        v-model="$v.windings.$each[index].admissibleWaterOnPaper.$model"
                        :numeric-options="getNumericOptions('admissibleWaterOnPaper')"
                        :validation="$v.windings
                          .$each[index]
                          .admissibleWaterOnPaper"
                        :invalid="$v.windings
                          .$each[index]
                          .admissibleWaterOnPaper
                          .$error"
                        :disabled="isDisabled"
                      />
                      <b-form-invalid-feedback
                        v-if="$v.windings.$each[index].admissibleWaterOnPaper.$error"
                        id="input-live-feedback-admissible-water-on-paper"
                      >
                        <span v-html="getValidationFeedback(
                          'windings',
                          'admissibleWaterOnPaper',
                          index
                        )"></span>
                      </b-form-invalid-feedback>
                    </s-input-label-edit>
                  </div>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>
      </div>
    </div>
    <gas-volume-calculation-modal
      v-model="showModalVgCalculator"
      :is-visualization-mode="!editMode"
      :gas-volume="parameters.gasVolume"
      @calculate="onVgCalculateHandler"
    />
  </div>
</template>

<script>
import { EquilibriumCurve, OilType } from 'sigma-mappings';
import InfoBadge from '@/components/icon/InfoBadge';
import parametersFields
  from '@/domain/data/installation/engineering/waterOnPaper/parametersFields';
import validations
  from '@/domain/data/installation/engineering/waterOnPaper/validations';
import oilSolubilityConstantByType
  from '@/domain/data/installation/engineering/waterOnPaper/oilSolubilityConstantByType';
import GasVolumeCalculationModal
  from '@/domain/components/installation/engineering/waterOnPaper/GasVolumeCalculationModal';
import enumFilter from '@/filters/enumFilter';

const OIL_TYPES = [OilType.MINERAL, OilType.SILICONE, OilType.VEGETAL];

export default {
  name: 'WaterOnPaperParametersTab',
  props: {
    parameters: {
      type: Object,
      required: true,
    },
    originalData: {
      type: Object,
      default: () => ({}),
    },
    windings: {
      type: Array,
      required: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    InfoBadge,
    GasVolumeCalculationModal,
  },
  data: () => ({
    equilibriumCurve: EquilibriumCurve,
    showModalVgCalculator: false,
  }),
  validations() {
    return validations(this.parameters);
  },
  computed: {
    hasUserPermission() {
      return this.$hasUserPermission('PER013');
    },
    shouldDisableOilSolubilityConstant() {
      return this.isSaving
        || (this.parameters.shouldConsiderOilType
          && OIL_TYPES.indexOf(this.parameters.oilType) !== -1);
    },
    isInvalid() {
      return this.$v.$invalid;
    },
    isDisabled() {
      return !this.editMode || this.isSaving;
    },
  },
  filters: {
    enumFilter,
  },
  methods: {
    formatNumberValue(value, fieldName) {
      if (typeof value !== 'number') {
        return value;
      }
      return this.$n(value, {
        minimumFractionDigits: parametersFields[fieldName].precision,
        maximumFractionDigits: parametersFields[fieldName].precision,
      });
    },
    getLabel(fieldName) {
      return this.$t(parametersFields[fieldName].label);
    },
    getNumericOptions(fieldName) {
      return {
        decimalPlaces: parametersFields[fieldName].precision || 0,
      };
    },
    getValidationFeedback(varName, fieldName) {
      return this.$t('messages.MA086.message',
        {
          minValue: this.formatNumberValue(parametersFields[fieldName].minValue, fieldName),
          maxValue: this.formatNumberValue(parametersFields[fieldName].maxValue, fieldName),
        });
    },
    disableChangeConsiderOilTypeHandler() {
      this.$pushAlert({
        code: 'MA025',
        messageParams: {
          NomeDoCampo: this.$t('fields.asset.transformer.oil-type'),
        },
      });
      this.$nextTick(() => {
        this.$set(this.parameters, 'shouldConsiderOilType', false);
      });
    },
    setConstantByOilType() {
      let value;

      switch (this.parameters.oilType) {
        case OilType.MINERAL:
          value = oilSolubilityConstantByType.MINERAL;
          break;
        case OilType.SILICONE:
          value = oilSolubilityConstantByType.SILICONE;
          break;
        case OilType.VEGETAL:
          value = oilSolubilityConstantByType.VEGETAL;
          break;
        default:
          value = this.parameters.oilSolubilityConstant;
          break;
      }

      this.$nextTick(() => {
        this.$set(this.parameters, 'oilSolubilityConstant', value);
        this.$emit('force-rerender-form');
      });
    },
    onChangeConsiderOilTypeHandler(state) {
      const hasOilType = OIL_TYPES.indexOf(this.parameters.oilType) !== -1;
      if (!state) {
        const value = (this.originalData.parameters || {}).oilSolubilityConstant;
        this.$nextTick(() => {
          this.$set(this.parameters, 'oilSolubilityConstant', value);
          this.$emit('force-rerender-form');
        });
        return;
      }

      if (!hasOilType) {
        this.disableChangeConsiderOilTypeHandler();
        return;
      }

      this.setConstantByOilType();
    },
    onClickVgCalculatorHandler() {
      this.showModalVgCalculator = true;
    },
    onVgCalculateHandler({ gasVolume }) {
      this.$set(this.parameters, 'gasVolume', gasVolume);
      this.$emit('force-rerender-form');
    },
    windingClassMarginBottom(index) {
      return {
        'mb-3': (index + 1) < this.windings.length,
      };
    },
  },
  watch: {
    isInvalid: {
      handler(newValue) {
        this.$emit('invalid-form', newValue);
      },
      immediate: true,
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"
  @import "src/styles/defaults/mobile-first"

  .water-on-paper-parameters
    &__loader
      height: 100px
    &-general
      padding: 5px
      border: 1px solid $color-border-gray
      border-radius: 3px
      &__title
        padding: .35rem 1rem
        border-radius: 5px
        font-weight: bold
        background-color: var(--color-background-dark)
      &__box
        border: 1px solid $color-border-gray
        border-radius: 3px
      &__input
        & .label-edit_editing > input
          text-align: right
        & .label-edit_viewing
          opacity: .65
      &__select
        & .label-edit_viewing
          opacity: .65
      &__calculator
        position: absolute
        top: 0
        left: 9.5em
        vertical-align: bottom
        &-button
          line-height: 1
          &:hover
            background-color: transparent
        &-icon
          font-size: 2em
          cursor: pointer
          color: var(--color-secondary)
        &-tooltip
          vertical-align: top

  +media-min-md()
    .water-on-paper-parameters
      &-general
        &__input
          & .label-edit_editing > input
            max-width: 9em
  +media-max-sm()
    .water-on-paper-parameters
      &-general
        &__input
          & .label-edit_editing > input
            max-width: 100%
          &.water-on-paper-parameters-general__gas-volume-input > .label-edit_editing > input
            max-width: calc(100% - 54px - 1rem)
        &__calculator
          left: auto
          right: 0
  @media print
    .water-on-paper-parameters
      &-general
        &__input
          &.water-on-paper-parameters-general__gas-volume-input > .label-edit_editing > input
            max-width: 100%
</style>
